import { useReducer } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import requestStatus from '../constants/requestStatus';
import userRoleType from '../constants/userRoleType';
import useNavigationLinks from './useNavigationLinks';
import useIdentityService from './useIdentityService';
import useApi from './useApi';

const initialAuthModel = {
  sessionRequest: requestStatus.REQUEST_NOT_INITIATED,
  currentOrganizationId: null,
  user: {
    id: 0,
    userName: null,
    name: null,
  },
  userMissions: [],
  userRoles: [],
  userRolePermissions: [],
  userCurrentMissionRoles: [],
  loading: true,
  build: null,
  env: null,
  privacyStatementRead: false,
  piaApproved: false,
};

const sessionReducer = (state, action) => {
  switch (action.type) {
    case 'START_SESSION_REQUEST':
      return { ...state, sessionRequest: requestStatus.REQUEST_IN_PROGRESS };

    case 'SET_USER_DATA':
      return {
        ...state,
        ...action.data,
        sessionRequest: requestStatus.REQUEST_COMPLETED,
      };

    case 'LOGOUT_USER':
      return { ...initialAuthModel };

    case 'UPDATE_USER_CURRENT_MISSION_ROLES':
      const role1 = state.userRoles.filter(
          (item) =>
            item.organizationId === Number(state.currentOrganizationId)
        );
      const role2 = state.userRoles.filter(
          (item) =>
            !item.organizationId
        );
      const newCurrentRoles = role1.concat(role2);  
      return {
        ...state,
        userCurrentMissionRoles: newCurrentRoles
      };
    case 'SET_SEGMENTS':
      return {
        ...state,
        [action.urlKey]: action.urlString,
      };
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};

const useSession = () => {
  const [sessionStore, sessionDispatch] = useReducer(
    sessionReducer,
    initialAuthModel
  );
  const { oktaAuth, authState } = useOktaAuth();
  const { goHome, goTo, goLogout } = useNavigationLinks();
  const identityService = useIdentityService();
  const api = useApi();
  const setUserData = async () => {
    const userInfo = await oktaAuth.getUser();
    const oktaToken = await oktaAuth.getAccessToken();

    sessionDispatch({ type: 'START_SESSION_REQUEST' });

    api.currentOrganizationId = sessionStorage.getItem(
      'selectedOrganizationId'
    );
    const response = await identityService.getIdentity();
    if (response && response.ok) {
      let newOrganizationId = Number(response.data.currentOrganizationId);
      let piaApproved = Boolean(
        sessionStorage.getItem(`mc-PIA-approved`) ?? false
      );
      sessionStore.piaApproved = piaApproved;
      sessionStore.currentOrganizationId = newOrganizationId;
      api.currentOrganizationId = newOrganizationId;
      sessionDispatch({
        type: 'SET_USER_DATA',
        data: {
          applicationVersion: response.data.applicationVersion,
          currentOrganizationId: newOrganizationId,
          user: {
            id: response.data.user.id,
            userName: response.data.user.userName,
            name: response.data.user.name,
          },
          userMissions: response.data.userMissions,
          userRoles: response.data.userRoles,
          userRolePermissions: response.data.userRolePermissions,
          userCurrentMissionRoles: response.data.userRoles.map(
            (item) =>
              (!item.organizationId ||
                item.organizationId === newOrganizationId) &&
              item
          ),
          loading: false,
        },
      });
    }
    //else {
    //   let newOrganizationId = 0;
    //   let piaApproved = Boolean(
    //     sessionStorage.getItem(`mc-PIA-approved`) ?? false
    //   );
    //   sessionStore.piaApproved = piaApproved;
    //   sessionStore.currentOrganizationId = newOrganizationId;
    //   api.currentOrganizationId = newOrganizationId;
    //   sessionDispatch({
    //     type: 'SET_USER_DATA',
    //     data: {
    //       applicationVersion: 0,
    //       currentOrganizationId: newOrganizationId,
    //       user: {
    //         id: 0,
    //         userName: userInfo.email,
    //         name: `${userInfo.given_name}, ${userInfo.family_name}`,
    //       },
    //       userMissions: [],
    //       userRoles: [],
    //       userRolePermissions: [],
    //       userCurrentMissionRoles: [],
    //       loading: false,
    //     },
    //   });

    // }
  };

  const isAuthenticated = () => {
    try {
      return (
        authState &&
        authState?.isAuthenticated &&
        sessionStore?.sessionRequest === requestStatus.REQUEST_COMPLETED
      );
    } catch (e) {
      console.log('[isAuthenticated error]', e);
      return false;
    }
  };

  const isGlobalAdmininstrator =
    (authState &&
      authState?.isAuthenticated &&
      sessionStore?.sessionRequest === requestStatus.REQUEST_COMPLETED &&
      sessionStore?.userRoles?.some(
        (r) => r.role === userRoleType.GlobalAdministrator
      )) ??
    false;

  const initSession = async () => {
    console.log('init session');
    try {
      await setUserData();
    } catch (e) {
      console.log('[init session error]', e);
    }
  };

  const logoutSession = async () => {
    try {
      sessionDispatch({ type: 'LOGOUT_USER' });
      await identityService.loggedOut();
      oktaAuth.tokenManager.clear();
      oktaAuth.signOut();
    } catch (e) {
      console.log('[logoutSession error]', e);
    } finally {
      goLogout();
    }
  };

  const switchMission = async (orgId) => {
    sessionStore.currentOrganizationId = Number(orgId);
    api.currentOrganizationId = sessionStore.currentOrganizationId;
    sessionStorage.setItem('selectedOrganizationId', Number(orgId));
    sessionDispatch({ type: 'UPDATE_USER_CURRENT_MISSION_ROLES' });
    goHome();
  };

  const setSegments = async (urlKey, urlString) => {
    sessionDispatch({ type: 'SET_SEGMENTS', urlKey, urlString });
  };

  const isMissionAdmininstrator =
    (authState &&
      authState?.isAuthenticated &&
      sessionStore?.sessionRequest === requestStatus.REQUEST_COMPLETED &&
      sessionStore?.userCurrentMissionRoles?.some(
        (r) => r.role === userRoleType.MissionAdministrator
      )) ??
    false;

  const sessionHelpers = {
    isAuthenticated,
    isGlobalAdmininstrator,
    initSession,
    logoutSession,
    switchMission,
    setSegments,
    isMissionAdmininstrator,
  };

  return { sessionStore, sessionHelpers };
};

export default useSession;
